import "./index.css";
import "./default.style.css";
import "./action.style.css";
import "./highlight.style.css";
import "./success.style.css";
import "./header.style.css";

import React, {useRef} from "react";
import Toggle from "react-toggle";
import {getBoolean, getString} from "Core/helpers/data";
import {TOGGLE_INPUT_LABEL_POSITION} from "Core/components/input/ToggleInput/const";
import {executeComponentCallback} from "Core/components/BaseComponent";
import Label from "Core/components/display/Label";

/**
 * Toggle input component
 * @description Toggle button input component.
 * @note This is a controlled component, which means it does not maintain its own state and value is controlled by the
 * parent component.
 *
 * This component uses the 'react-toggle' component.
 */
function ToggleInput({
	styleName = 'action', className = '', readOnly = false, disabled = false, checked = false, value = '', 
	disableAnimation = false, label = null, labelPosition = TOGGLE_INPUT_LABEL_POSITION.RIGHT, ...reactToggle
}) {
	const toggleRef = useRef(null);
	
	/**
	 * Render the actual toggle
	 * @return {JSX.Element}
	 */
	function renderToggle() {
		return (
			<Toggle
				{...reactToggle}
				value={getString(value)}
				checked={getBoolean(checked)}
				className={
					`toggle-input-component ${readOnly ? 'readonly' : ''} ${disableAnimation ? 'no-animation' : ''} ` +
					`${className} ${styleName}-style`
				}
				disabled={readOnly ? true : disabled}
				ref={toggleRef}
			/>
		);
	}

	/**
	 * Render label if specified
	 * 
	 * @return {React.DetailedReactHTMLElement<{onClick: onClick}, HTMLElement>|JSX.Element}
	 */
	function renderLabel() {
		if (!label) {
			return null;
		} else if (typeof label === 'string' || typeof label === 'number') {
			return (
				<Label 
					content={getString(label)} 
					element="span" 
					elementProps={{
						className: `toggle-input-component-label ${labelPosition}`,
						onClick: () => toggleRef.current?.input?.click(),
					}}
				/>
			);
		} else {
			return React.cloneElement(label, {
				className: `toggle-input-component-label ${labelPosition} ${getString(label, 'className')}`,
				onClick: e => {
					toggleRef.current?.input.click();
					executeComponentCallback(label.onClick, e);
				}
			});
		}
	}
	
	return (
		label ?
			<>
				{labelPosition === TOGGLE_INPUT_LABEL_POSITION.LEFT ? renderLabel() : null}
				{renderToggle()}
				{labelPosition === TOGGLE_INPUT_LABEL_POSITION.RIGHT ? renderLabel() : null}
			</>
			:
			renderToggle()
	);
}

export * from "./const";
export default ToggleInput;